import React, { useEffect, useMemo, useRef, useState } from "react";

type FilterValue = { id: number; name: string; count?: number };
type FilterGroup = { name: string; values: FilterValue[] };
type FiltersMap = Record<string, FilterGroup>; // ключи типа "a33"
type SelectedMap = Record<string, Record<number, boolean>>; // "attribute-33": { 12: true, 13:false }

type Props = {
    filters: FiltersMap | null;
    values: Record<string, number[]>; // "attribute-33": [ids...]
    onCallback: (next: Record<string, number[]>) => void;
    className?: string;
    initiallyOpen?: number; // сколько фасетов открывать по умолчанию
    debounceMs?: number;
};

function toSelectedMap(values: Props["values"]): SelectedMap {
    const out: SelectedMap = {};
    Object.entries(values ?? {}).forEach(([k, arr]) => {
        out[k] = {};
        if (arr && arr.length > 0) {
            (arr || []).forEach((id) => (out[k][id] = true));
        }
    });
    return out;
}

function toArrayMap(selected: SelectedMap): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    Object.entries(selected).forEach(([k, obj]) => {
        out[k] = Object.keys(obj)
            .filter((id) => obj[+id])
            .map((id) => +id);
    });
    return out;
}

export default function Filters({
    filters,
    values,
    onCallback,
    className = "",
    initiallyOpen = 2,
    debounceMs = 350,
}: Props) {
    // локальный стейт выбранных значений
    const [selected, setSelected] = useState<SelectedMap>(() => toSelectedMap(values));
    // открытые аккордеоны по индексу
    const [openIdx, setOpenIdx] = useState<Record<number, boolean>>({});
    const initialIgnore = useRef(true)

    // useEffect(() => {
    //     // setSelected(toSelectedMap(values));
    // }, [values]);

    // дебаунс применения фильтров
    useEffect(() => {
        if (initialIgnore.current === true) {
            initialIgnore.current = false;

            return;
        }

        const t = setTimeout(() => {
            onCallback(toArrayMap(selected));
        }, 350);

        return () => clearTimeout(t);
    }, [selected, onCallback]);

    // подготовим список фасетов
    const entries = useMemo(() => {
        if (!filters) return [];
        return Object.keys(filters).map((rawKey) => {
            const fg = filters[rawKey];
            let attributeId = rawKey.startsWith("a") ? rawKey.slice(1) : rawKey; // "a33" -> "33"
            attributeId = attributeId.startsWith("-") ? attributeId.slice(1) : attributeId; // "a33" -> "33"
            const groupKey = `attribute-${attributeId}`;
            return { rawKey, attributeId, groupKey, ...fg };
        });
    }, [filters]);

    const toggle = (groupKey: string, id: number, checked: boolean) => {
        setSelected((prev) => ({
            ...prev,
            [groupKey]: { ...(prev[groupKey] || {}), [id]: checked },
        }));
    };

    const resetGroup = (groupKey: string) => {
        setSelected((prev) => ({ ...prev, [groupKey]: {} }));
    };

    const clearAll = () => {
        setSelected({});
    };

    if (!entries.length) return null;

    return (
        <div className={`flex flex-col gap-y-3 ${className}`}>
            {/* Applied bar */}
            <AppliedBar selected={selected} onRemove={(gk, id) => toggle(gk, id, false)} onClear={clearAll} />

            {entries.map((e, idx) => (
                <Facet
                    key={e.groupKey}
                    title={e.name}
                    defaultOpen={idx < initiallyOpen}
                    open={openIdx[idx]}
                    onToggle={() => setOpenIdx((o) => ({ ...o, [idx]: !o[idx] }))}
                >
                    <FacetBody
                        groupKey={e.groupKey}
                        values={e.values}
                        selected={selected[e.groupKey] || {}}
                        onToggle={toggle}
                        onReset={() => resetGroup(e.groupKey)}
                    />
                </Facet>
            ))}
        </div>
    );
}

/* ---------- Вспомогательные компоненты ---------- */

function AppliedBar({
    selected,
    onRemove,
    onClear,
}: {
    selected: SelectedMap;
    onRemove: (groupKey: string, id: number) => void;
    onClear: () => void;
}) {
    const chips: Array<{ gk: string; id: number; label: string }> = [];

    Object.entries(selected).forEach(([gk, obj]) => {
        Object.entries(obj).forEach(([id, v]) => {
            if (v) chips.push({ gk, id: +id, label: String(id) }); // label заменим ниже в FacetBody (у нас тут нет имён)
        });
    });

    if (!chips.length) return null;
    // Показываем только «очистить всё», реальные метки чипов показываются в FacetBody (там есть имена)
    return (
        <div className="flex items-center justify-between rounded border p-2">
            <div className="text-sm text-gray-600">Filters applied</div>
            <button className="text-sm text-blue-600 hover:underline" onClick={onClear}>
                Clear all
            </button>
        </div>
    );
}

function Facet({
    title,
    children,
    defaultOpen = false,
    open,
    onToggle,
}: {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
    open?: boolean;
    onToggle?: () => void;
}) {
    const [internal, setInternal] = useState(defaultOpen);
    const isOpen = open ?? internal;

    const toggle = () => {
        onToggle ? onToggle() : setInternal((o) => !o);
    };

    return (
        <div className="border-b">
            <button
                className="flex w-full items-center justify-between py-2"
                onClick={toggle}
                aria-expanded={isOpen}
            >
                <span className="font-medium">{title}</span>
                <span className="text-gray-500">{isOpen ? "–" : "+"}</span>
            </button>
            {isOpen && <div className="pb-3">{children}</div>}
        </div>
    );
}

function FacetBody({
    groupKey,
    values,
    selected,
    onToggle,
    onReset,
}: {
    groupKey: string;
    values: FilterValue[];
    selected: Record<number, boolean>;
    onToggle: (gk: string, id: number, checked: boolean) => void;
    onReset: () => void;
}) {
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(8);

    const filtered = useMemo(() => {
        if (!query.trim()) return values;
        const q = query.toLowerCase();
        return values.filter((v) => v.name.toLowerCase().includes(q));
    }, [values, query]);

    const visible = filtered.slice(0, limit);
    const hasMore = filtered.length > limit;

    return (
        <div className="flex flex-col gap-2">
            {values.length > 10 && (
                <input
                    className="w-full rounded border px-2 py-1 text-sm"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            )}

            <ul className="flex flex-col gap-1">
                {visible.map((v) => (
                    <li key={`${groupKey}-${v.id}`} className="flex items-center gap-2">
                        <input
                            id={`${groupKey}-${v.id}`}
                            type="checkbox"
                            checked={!!selected[v.id]}
                            onChange={(e) => onToggle(groupKey, v.id, e.target.checked)}
                        />
                        <label htmlFor={`${groupKey}-${v.id}`} className="cursor-pointer">
                            {v.name}{" "}
                            {typeof v.count === "number" && (
                                <span className="text-xs text-gray-500">({v.count})</span>
                            )}
                        </label>
                    </li>
                ))}
            </ul>

            {hasMore && (
                <button
                    className="self-start text-sm text-blue-600 hover:underline"
                    onClick={() => setLimit((n) => n + 10)}
                >
                    Show more
                </button>
            )}

            <div className="pt-1">
                <button className="text-sm text-gray-600 hover:underline" onClick={onReset}>
                    Reset
                </button>
            </div>
        </div>
    );
}